import { useState, useCallback, useRef } from 'react';
import { figmaApi } from '../api/figmaApi';
import { FigmaFileStructure, FigmaFrame, FigmaPage } from '../types/figma';

interface UseFigmaApiReturn {
  fileStructure: FigmaFileStructure | null;
  pages: FigmaPage[];
  frames: FigmaFrame[];
  frameThumbnails: Record<string, string>;
  loading: boolean;
  error: string | null;
  loadFile: (fileKey: string) => Promise<FigmaFileStructure | null>;
  loadFrameNodes: (fileKey: string, frameId: string) => Promise<any>;
  loadFrameThumbnails: (fileKey: string, frames: FigmaFrame[]) => Promise<void>;
  loadFrameImageUrl: (fileKey: string, frameId: string) => Promise<string | null>;
}

function extractFrames(pages: FigmaPage[]): FigmaFrame[] {
  const frames: FigmaFrame[] = [];
  for (const page of pages) {
    for (const child of page.children || []) {
      if (child.type === 'FRAME' || child.type === 'COMPONENT' || child.type === 'COMPONENT_SET') {
        frames.push(child as FigmaFrame);
      }
    }
  }
  return frames;
}

export function useFigmaApi(): UseFigmaApiReturn {
  const [fileStructure, setFileStructure] = useState<FigmaFileStructure | null>(null);
  const [pages, setPages] = useState<FigmaPage[]>([]);
  const [frames, setFrames] = useState<FigmaFrame[]>([]);
  const [frameThumbnails, setFrameThumbnails] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const imageUrlCache = useRef<Map<string, string>>(new Map());
  const loadingFileRef = useRef<string | null>(null);

  const loadFile = useCallback(async (fileKey: string) => {
    // Prevent duplicate concurrent calls for the same file
    if (loadingFileRef.current === fileKey) {
      return null;
    }
    loadingFileRef.current = fileKey;
    setFileStructure(null);
    setPages([]);
    setFrames([]);
    setFrameThumbnails({});
    setLoading(true);
    setError(null);
    try {
      const data = await figmaApi.getFile(fileKey);
      setFileStructure(data);
      const filePages = data.document?.children || [];
      setPages(filePages);
      setFrames(extractFrames(filePages));
      return data;
    } catch (err: any) {
      const msg = err?.response?.data?.message || err.message || 'Failed to load Figma file';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
      loadingFileRef.current = null;
    }
  }, []);

  /**
   * Batch-fetch thumbnail URLs for frame selector.
   * Calls Figma images API via proxy, gets S3 URLs, uses them directly.
   */
  const loadFrameThumbnails = useCallback(async (fileKey: string, frameList: FigmaFrame[]) => {
    if (frameList.length === 0) return;
    try {
      const batchSize = 50;
      const allThumbnails: Record<string, string> = {};

      for (let i = 0; i < frameList.length; i += batchSize) {
        const batch = frameList.slice(i, i + batchSize);
        const nodeIds = batch.map(f => f.id).join(',');
        const urls = await figmaApi.getImageUrls(fileKey, nodeIds, 'png', 1);
        Object.assign(allThumbnails, urls);
      }

      setFrameThumbnails(allThumbnails);
    } catch (err) {
      console.warn('Failed to load frame thumbnails:', err);
    }
  }, []);

  /**
   * Fetch the rendered image URL for a single frame (full resolution).
   * Matches POC approach: call images API, get URL, use it directly as img src.
   */
  const loadFrameImageUrl = useCallback(async (fileKey: string, frameId: string): Promise<string | null> => {
    const cacheKey = `${fileKey}-${frameId}`;
    const cached = imageUrlCache.current.get(cacheKey);
    if (cached) return cached;

    try {
      const urls = await figmaApi.getImageUrls(fileKey, frameId, 'png', 2);
      const url = urls[frameId] || null;
      if (url) {
        imageUrlCache.current.set(cacheKey, url);
      }
      return url;
    } catch (err) {
      console.error('Failed to load frame image URL:', err);
      return null;
    }
  }, []);

  const loadFrameNodes = useCallback(async (fileKey: string, frameId: string) => {
    return figmaApi.getFileNodes(fileKey, frameId);
  }, []);

  return {
    fileStructure,
    pages,
    frames,
    frameThumbnails,
    loading,
    error,
    loadFile,
    loadFrameNodes,
    loadFrameThumbnails,
    loadFrameImageUrl,
  };
}
